import type { BuildingInteriorSpec } from "@cold-case/shared/interior-layout";
import type { InteriorTheme } from "@cold-case/shared/building-interior-theme";
import { TILE_SIZE } from "@cold-case/shared/constants";

function lerpChannel(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function lerpColor(c0: number, c1: number, t: number): number {
  const u = Math.max(0, Math.min(1, t));
  const r0 = (c0 >> 16) & 0xff;
  const g0 = (c0 >> 8) & 0xff;
  const b0 = c0 & 0xff;
  const r1 = (c1 >> 16) & 0xff;
  const g1 = (c1 >> 8) & 0xff;
  const b1 = c1 & 0xff;
  return (
    (lerpChannel(r0, r1, u) << 16) |
    (lerpChannel(g0, g1, u) << 8) |
    lerpChannel(b0, b1, u)
  );
}

function colorToCss(c: number): string {
  const hex = (c & 0xffffff).toString(16).padStart(6, "0");
  return `#${hex}`;
}

function floorFillForTile(
  theme: InteriorTheme,
  tx: number,
  ty: number,
  localX: number,
  localY: number
): number {
  const { floorA, floorB } = theme.palette;
  const checker = (tx + ty) % 2 === 0;
  let fill = lerpColor(floorA, floorB, checker ? 0.35 : 0.65);

  switch (theme.pattern) {
    case "plank": {
      const band = Math.floor(localY / 8) % 2 === 0;
      fill = lerpColor(fill, floorB, band ? 0.12 : 0);
      break;
    }
    case "tileGrid": {
      const grout = localX % 32 === 0 || localY % 32 === 0;
      if (grout) fill = lerpColor(fill, theme.palette.wallHi, 0.35);
      break;
    }
    case "slab": {
      const subtle = (tx * 7 + ty * 11) % 5 === 0;
      fill = lerpColor(fill, floorB, subtle ? 0.08 : 0);
      break;
    }
    case "checker":
    default:
      break;
  }
  return fill;
}

/** Bakes interior floor + wall fill for one building theme. */
export function bakeInteriorGroundCanvas(
  spec: BuildingInteriorSpec,
  theme: InteriorTheme
): HTMLCanvasElement {
  const wPx = spec.gridW * TILE_SIZE;
  const hPx = spec.gridH * TILE_SIZE;
  const canvas = document.createElement("canvas");
  canvas.width = wPx;
  canvas.height = hPx;
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;

  const { wall, wallHi, wallBaseboard } = theme.palette;

  for (let ty = 0; ty < spec.gridH; ty++) {
    for (let tx = 0; tx < spec.gridW; tx++) {
      const walk = spec.walkable[ty]![tx]!;
      const px = tx * TILE_SIZE;
      const py = ty * TILE_SIZE;
      const checker = (tx + ty) % 2 === 0;

      if (walk) {
        for (let sy = 0; sy < TILE_SIZE; sy++) {
          for (let sx = 0; sx < TILE_SIZE; sx++) {
            const fill = floorFillForTile(theme, tx, ty, sx, sy);
            ctx.fillStyle = colorToCss(fill);
            ctx.fillRect(px + sx, py + sy, 1, 1);
          }
        }
      } else {
        const fill = lerpColor(wall, wallHi, checker ? 0.2 : 0.5);
        ctx.fillStyle = colorToCss(fill);
        ctx.fillRect(px, py, TILE_SIZE, TILE_SIZE);
        ctx.strokeStyle = colorToCss(wallHi);
        ctx.lineWidth = 2;
        ctx.strokeRect(px + 1, py + 1, TILE_SIZE - 2, TILE_SIZE - 2);
        if (wallBaseboard) {
          ctx.fillStyle = colorToCss(wallHi);
          ctx.fillRect(px + 2, py + TILE_SIZE - 8, TILE_SIZE - 4, 6);
        }
      }
    }
  }
  return canvas;
}

/** Sample center pixel of a walkable interior tile (for tests). */
export function sampleInteriorFloorColor(
  canvas: HTMLCanvasElement,
  tx: number,
  ty: number
): { r: number; g: number; b: number } {
  const x = tx * TILE_SIZE + Math.floor(TILE_SIZE / 2);
  const y = ty * TILE_SIZE + Math.floor(TILE_SIZE / 2);
  const ctx = canvas.getContext("2d");
  if (!ctx) return { r: 0, g: 0, b: 0 };
  const d = ctx.getImageData(x, y, 1, 1).data;
  return { r: d[0]!, g: d[1]!, b: d[2]! };
}
